import type { SwitchEffect, SwitchEvent, SwitchState } from './switch-machine';
import type { WorkspaceLifecycleHandler, WorkspaceRuntime, WorkspaceRuntimeStore } from './types';
import { computed, nextTick, shallowReadonly, shallowRef } from 'vue';
import { runIsolated } from '#layers/base/public';
import { reduceSwitch } from './switch-machine';

const RUNTIME_RETIREMENT_TIMEOUT_MS = 5000;

/** Listener sets by manager, so the test reset helper can reach a closed-over set without a production method. */
const transitionListenerSets = new WeakMap<object, Set<WorkspaceLifecycleHandler>>();

export function clearTransitionListeners(manager: object) {
	transitionListenerSets.get(manager)?.clear();
}

export type WorkspaceRuntimeManager<TStore extends WorkspaceRuntimeStore> = ReturnType<typeof createWorkspaceRuntimeManager<TStore>>;

export interface SwitchRuntimeOptions<TStore extends WorkspaceRuntimeStore, TPrepared> {
	createRuntime: (slug: string) => Promise<WorkspaceRuntime<TStore>>;
	prepare: (runtime: WorkspaceRuntime<TStore>) => Promise<TPrepared>;
	publish: (prepared: TPrepared, runtime: WorkspaceRuntime<TStore>) => void;
	slug: string;
}

export interface LeaveWorkspaceOptions {
	/** Clears the published routes; runs before the `left` transition reaches listeners. */
	unpublish?: () => void;
}

/** Expected control-flow signal for navigation that lost a latest-switch-wins race. */
export class WorkspaceSwitchSupersededError extends Error {
	constructor(slug: string) {
		super(`Workspace runtime preparation for "${slug}" was superseded`);
		this.name = 'WorkspaceSwitchSupersededError';
	}
}

/** Executes the effects `reduceSwitch` emits and settles each `switchRuntime` caller's promise (ADR 049). */
export function createWorkspaceRuntimeManager<TStore extends WorkspaceRuntimeStore>() {
	type Runtime = WorkspaceRuntime<TStore>;
	type State = SwitchState<Runtime>;
	type Event = SwitchEvent<Runtime, unknown>;
	type Effect = SwitchEffect<Runtime, unknown>;

	interface Candidate {
		createRuntime: (slug: string) => Promise<Runtime>;
		prepare: (runtime: Runtime) => Promise<unknown>;
		publish: (prepared: unknown, runtime: Runtime) => void;
		settled: PromiseWithResolvers<Runtime>;
	}

	const state = shallowRef<State>({ phase: 'idle' });
	const pending = new Map<number, Candidate>();
	const disposed = new WeakSet<Runtime>();
	const listeners = new Set<WorkspaceLifecycleHandler>();
	let nextCandidateId = 0;

	const activeRuntime = computed<Runtime | null>(() => {
		const current = state.value;
		if (current.phase === 'idle') return null;
		return current.phase === 'preparing' ? current.outgoing : current.runtime;
	});

	function onTransition(listener: WorkspaceLifecycleHandler) {
		listeners.add(listener);
		return () => listeners.delete(listener);
	}

	function isCurrentCandidate(candidateId: number) {
		return state.value.phase === 'preparing' && state.value.candidate.id === candidateId;
	}

	function requireCandidate(candidateId: number) {
		const candidate = pending.get(candidateId);
		if (!candidate) throw new Error(`Workspace switch candidate ${candidateId} is not pending`);
		return candidate;
	}

	function takeCandidate(candidateId: number) {
		const candidate = requireCandidate(candidateId);
		pending.delete(candidateId);
		return candidate;
	}

	function dispatch(event: Event, leaveOptions: LeaveWorkspaceOptions = {}) {
		const previous = state.value;
		const { state: next, effects } = reduceSwitch(previous, event);

		// State lands before effects run, so a re-entrant dispatch from `publish` observes `publishing`.
		state.value = next;
		settleCandidates(previous, next, event);
		for (const effect of effects) runEffect(effect, leaveOptions);
	}

	/**
	 * Settles the promise of any candidate the applied row left behind; `prepared` hands its candidate to `publishing`.
	 * `publishing` exits exactly once: the publish effect dispatches either `published` or `publishFailed`, never both,
	 * so a candidate cannot be resolved and later rejected.
	 */
	function settleCandidates(previous: State, next: State, event: Event) {
		if (previous.phase === 'preparing') {
			const { id, slug } = previous.candidate;
			const kept = (next.phase === 'preparing' && next.candidate.id === id) || next.phase === 'publishing';
			if (kept) return;

			takeCandidate(id).settled.reject(event.type === 'candidateFailed' ? event.error : new WorkspaceSwitchSupersededError(slug));
		}

		if (previous.phase === 'publishing') {
			const { settled } = takeCandidate(previous.candidateId);

			if (next.phase === 'active') settled.resolve(next.runtime);
			else if (event.type === 'publishFailed') settled.reject(event.error);
		}
	}

	/** Feeds an async candidate step back into the machine, ignoring completions that lost the latest-switch-wins race. */
	function awaitCandidateStep<TValue>(candidateId: number, step: Promise<TValue>, onCompleted: (value: TValue) => void) {
		const { settled } = requireCandidate(candidateId);

		void step.then(
			onCompleted,
			(error: unknown) => {
				if (isCurrentCandidate(candidateId)) dispatch({ type: 'candidateFailed', candidateId, error });
			},
		).catch(settled.reject);
	}

	function runEffect(effect: Effect, { unpublish }: LeaveWorkspaceOptions) {
		switch (effect.type) {
			case 'createRuntime': {
				const { candidateId, slug } = effect;

				awaitCandidateStep(candidateId, requireCandidate(candidateId).createRuntime(slug), (runtime) => {
					if (isCurrentCandidate(candidateId)) dispatch({ type: 'runtimeCreated', candidateId, runtime });
					else dispose(runtime);
				});

				break;
			}

			case 'prepare': {
				const { candidateId, runtime } = effect;

				awaitCandidateStep(candidateId, requireCandidate(candidateId).prepare(runtime), (prepared) => {
					if (isCurrentCandidate(candidateId)) dispatch({ type: 'prepared', candidateId, prepared });
				});

				break;
			}

			case 'publish': {
				const current = state.value;
				if (current.phase !== 'publishing') throw new Error('Publish effect outside the publishing phase');

				try {
					requireCandidate(current.candidateId).publish(effect.prepared, current.runtime);
				}
				catch (error) {
					dispatch({ type: 'publishFailed', error });
					break;
				}

				dispatch({ type: 'published' });
				break;
			}

			case 'unpublish':
				unpublish?.();
				break;

			case 'dispose':
				dispose(effect.runtime);
				break;

			case 'retire':
				retire(effect.runtime);
				break;

			case 'notify':
				// Snapshot so listeners (un)registered mid-dispatch only affect the next transition; a failing listener must not unwind the publication.
				runIsolated(
					[...listeners].map((listener) => () => listener(effect.transition)),
					`One or more workspace lifecycle handlers failed on "${effect.transition.type}"`,
				);

				break;

			default:
				effect satisfies never;
		}
	}

	/**
	 * Retire = the displaced active runtime: publication/leave hides its subtree, so wait a tick for unmount hooks
	 * to register saves, close tracker intake, drain started operations up to the deadline, then dispose.
	 * `dispose` is idempotent (WeakSet) and is used directly for candidates that failed or lost the race.
	 */
	function retire(runtime: Runtime) {
		void (async () => {
			await nextTick();
			runtime.operations.close();
			await runtime.operations.waitForIdle({ timeoutMs: RUNTIME_RETIREMENT_TIMEOUT_MS });
			dispose(runtime);
		})();
	}

	function dispose(runtime: Runtime) {
		if (disposed.has(runtime)) return;
		disposed.add(runtime);

		runIsolated([
			runtime.operations.close,
			() => runtime.abortController.abort(),
			// Queries die with the scope before the cache resets, so the reset cannot schedule a refetch.
			() => runtime.scope.stop(),
			// `dispose` only stops rstore's tombstone GC interval, the one timer the cache owns; `clear` then
			// releases the entries.
			() => runtime.store.$cache.dispose(),
			() => runtime.store.$cache.clear(),
		], `Failed to fully dispose workspace runtime "${runtime.slug}"`);
	}

	/**
	 * Prepares `slug` in isolation, then publishes it only if this is still the latest requested switch.
	 * A repeat request for the slug already being prepared joins that preparation instead of restarting it.
	 * Invalid re-entry (for example from inside `publish`) throws synchronously.
	 */
	function switchRuntime<TPrepared>(
		options: SwitchRuntimeOptions<TStore, TPrepared>,
	): Promise<Runtime> {
		const { slug } = options;
		if (!slug) throw new Error('A workspace slug is required to prepare a workspace runtime');

		const candidateId = ++nextCandidateId;

		pending.set(candidateId, {
			createRuntime: options.createRuntime,
			prepare: options.prepare,
			publish: (prepared, runtime) => options.publish(prepared as TPrepared, runtime),
			settled: Promise.withResolvers<Runtime>(),
		});

		try {
			dispatch({ type: 'switch', slug, candidateId });
		}
		catch (error) {
			pending.delete(candidateId);
			throw error;
		}

		const current = state.value;

		if (current.phase === 'preparing') {
			if (current.candidate.id !== candidateId) pending.delete(candidateId);
			return requireCandidate(current.candidate.id).settled.promise;
		}

		pending.delete(candidateId);
		if (current.phase === 'active') return Promise.resolve(current.runtime);

		throw new Error(`Workspace switch to "${slug}" left the manager "${current.phase}"`);
	}

	/** Leaves workspace UI and retires the active runtime after already-started operations drain. */
	function leaveWorkspace(options: LeaveWorkspaceOptions = {}) {
		dispatch({ type: 'leave' }, options);
	}

	const manager = {
		activeRuntime,
		state: shallowReadonly(state),
		leaveWorkspace,
		onTransition,
		switchRuntime,
	};

	transitionListenerSets.set(manager, listeners);

	return manager;
}
